import { z } from "zod";
var ProductTypes;
(function(ProductTypes) {
    ProductTypes["goods"] = "goods";
    ProductTypes["services"] = "services";
})(ProductTypes || (ProductTypes = {}));
export const ProductSchema = (t)=>z.object({
        article_number: z.string().trim().min(1, {
            message: t("validation_article_number_required")
        }).max(50, {
            message: t("validation_article_number_max")
        }).refine((val)=>/^[A-Za-z0-9äöüßÄÖÜ_-]+$/.test(val), {
            message: t("validation_article_number_format")
        }),
        product_name: z.string().trim().min(1, {
            message: t("validation_product_name_required")
        }).refine((val)=>/^[A-Za-z0-9äöüßÄÖÜ ]+$/.test(val), {
            message: t("validation_product_name_format")
        }),
        product_type: z.nativeEnum(ProductTypes, {
            errorMap: (issue, ctx)=>{
                return {
                    message: t("validation_product_type_required")
                };
            }
        }),
        description: z.string().trim().min(1, {
            message: t("validation_description_required")
        }).max(255, {
            message: t("validation_description_max")
        }),
        unit: z.string().trim().min(1, {
            message: t("validation_unit_required")
        }),
        tax_rate: z.string().trim().min(1, {
            message: t("validation_tax_rate_required")
        }),
        netto_price: z.string().trim().min(1, {
            message: t("validation_netto_price_required")
        }).max(25, {
            message: t("validation_netto_price_max")
        }).refine((val)=>{
            // Match formatted European currency from formatStringToEuropeanCurrency:
            // Positive: "7.299,99 €" or "7,99 €"
            // Negative: "- 7.299,99 €" or "- 7,99 €"
            // Also allow unformatted: "7299.99" or "7299,99" or "7.99"
            const formattedPattern = /^(- )?\d{1,3}(?:\.\d{3})*(?:,\d{1,2})?\s*€$/;
            const unformattedPattern = /^-?\d+([,\.]\d{1,2})?$/;
            // val is already trimmed by the schema, so just test it
            return formattedPattern.test(val) || unformattedPattern.test(val);
        }, {
            message: t("validation_netto_price_format")
        })
    });
export const getProductTypeOptions = (t)=>[
        {
            label: t("product_type_goods"),
            value: "goods"
        },
        {
            label: t("product_type_services"),
            value: "services"
        }
    ];
export const getTranslatedProductType = (productType, t)=>{
    const typeMap = {
        "goods": t("product_type_goods"),
        "services": t("product_type_services")
    };
    return typeMap[productType] || productType;
};
export const getUnitOptions = (t)=>[
        {
            label: t("unit_stk"),
            value: "stk"
        },
        {
            label: t("unit_meter_square"),
            value: "meter_square"
        },
        {
            label: t("unit_meter"),
            value: "meter"
        },
        {
            label: t("unit_kg"),
            value: "kg"
        },
        {
            label: t("unit_t"),
            value: "t"
        },
        {
            label: t("unit_lfm"),
            value: "lfm"
        },
        {
            label: t("unit_pauschal"),
            value: "pauschal"
        },
        {
            label: t("unit_meter_cube"),
            value: "meter_cube"
        },
        {
            label: t("unit_std"),
            value: "std"
        },
        {
            label: t("unit_km"),
            value: "km"
        },
        {
            label: t("unit_percentage"),
            value: "percentage"
        },
        {
            label: t("unit_tag_e"),
            value: "tag_e"
        },
        {
            label: t("unit_litre"),
            value: "litre"
        }
    ];
export const getTranslatedUnitValue = (unitValue, t)=>{
    const unitMap = {
        "stk": t("unit_stk"),
        "meter_square": t("unit_meter_square"),
        "meter": t("unit_meter"),
        "kg": t("unit_kg"),
        "t": t("unit_t"),
        "lfm": t("unit_lfm"),
        "pauschal": t("unit_pauschal"),
        "meter_cube": t("unit_meter_cube"),
        "std": t("unit_std"),
        "km": t("unit_km"),
        "percentage": t("unit_percentage"),
        "tag_e": t("unit_tag_e"),
        "litre": t("unit_litre")
    };
    return unitMap[unitValue] || unitValue;
};
export const formatTaxRateDisplay = (taxRate, vatType)=>{
    if (taxRate === 0 && vatType) {
        if (vatType === "13b") {
            return "0% (\xa713b UStG)";
        } else if (vatType === "25a") {
            return "0% (\xa725a UStG)";
        }
    }
    return "".concat(taxRate, "%");
};
export const taxRateOptions = [
    {
        label: "0%",
        value: "0"
    },
    {
        label: "0% (\xa713b UStG)",
        value: "0_13b"
    },
    {
        label: "0% (\xa725a UStG)",
        value: "0_25a"
    },
    {
        label: "7%",
        value: "7"
    },
    {
        label: "19%",
        value: "19"
    }
];
// Example usage:
// const euroPriceString = formatToEuropeanCurrency(7299.99);
// console.log(euroPriceString); // Output: €7.299,99
export const formatToEuropeanCurrency = (numericValue)=>{
    // Convert the numeric value to a string with fixed decimal places
    const formattedString = numericValue.toFixed(2);
    // Split the string into integer and decimal parts
    const [integerPart, decimalPart] = formattedString.split(".");
    // Add the comma as a thousands separator
    const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    // Concatenate the euro sign, integer part, decimal comma, and decimal part
    const euroPriceString = "€".concat(formattedIntegerPart, ",").concat(decimalPart);
    return euroPriceString;
};
export const formatStringToEuropeanCurrency = (value)=>{
    if (value === null || value === undefined || value === "") return "";
    if (typeof value === "number" && !Number.isNaN(value)) {
        const isNegative = value < 0;
        const absValue = Math.abs(value);
        const [integerPart, decimalPart] = absValue.toFixed(2).split(".");
        const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        const formatted = "".concat(formattedIntegerPart, ",").concat(decimalPart, " €");
        return isNegative ? "- ".concat(formatted) : formatted;
    }
    let input = value.toString().trim();
    // Check if the input is negative
    const isNegative = input.startsWith("-");
    if (isNegative) {
        input = input.substring(1).trim(); // Remove the negative sign for processing
    }
    // Remove non-numeric characters
    const numericInput = input.replace(/[^0-9]/g, "");
    if (isNegative && numericInput.length === 0) {
        return "-";
    }
    // Format input as European currency
    let formattedValue = "";
    if (numericInput.length > 0) {
        let currencyValue = Number(numericInput) / 100; // Convert to euros
        // Check if the decimal part has more than two digits
        if (currencyValue % 1 !== 0 && currencyValue.toString().split(".")[1].length > 2) {
            currencyValue = parseFloat(currencyValue.toFixed(2)); // Round to two decimal places
        }
        // Format the currency value manually
        const [integerPart, decimalPart] = currencyValue.toFixed(2).split(".");
        const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        formattedValue = "".concat(formattedIntegerPart, ",").concat(decimalPart, " €");
        // Add the negative sign back if the input was negative
        if (isNegative) {
            formattedValue = "- ".concat(formattedValue);
        }
    }
    return formattedValue;
};
export const parseNettoPrice = (nettoPrice)=>{
    if (typeof nettoPrice === "number") {
        return Number.isFinite(nettoPrice) ? nettoPrice : 0;
    }
    const trimmed = (nettoPrice !== null && nettoPrice !== void 0 ? nettoPrice : "").toString().trim();
    if (!trimmed) return 0;
    const isNegative = trimmed.startsWith("-");
    const numericNettoPriceString = trimmed.replace(/^-\s*/, "").replace(/[€\s]/g, "").replace(/\./g, "").replace(",", ".");
    const numericNettoPrice = parseFloat(numericNettoPriceString);
    if (!Number.isFinite(numericNettoPrice)) return 0;
    return isNegative ? -numericNettoPrice : numericNettoPrice;
};
